function asyncGeneratorStep(gen, resolve, reject, _next, _throw, key, arg) {
    try {
        var info = gen[key](arg);
        var value = info.value;
    } catch (error) {
        reject(error);
        return;
    }
    if (info.done) {
        resolve(value);
    } else {
        Promise.resolve(value).then(_next, _throw);
    }
}
function _async_to_generator(fn) {
    return function() {
        var self = this, args = arguments;
        return new Promise(function(resolve, reject) {
            var gen = fn.apply(self, args);
            function _next(value) {
                asyncGeneratorStep(gen, resolve, reject, _next, _throw, "next", value);
            }
            function _throw(err) {
                asyncGeneratorStep(gen, resolve, reject, _next, _throw, "throw", err);
            }
            _next(undefined);
        });
    };
}
function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _object_without_properties(source, excluded) {
    if (source == null) return {};
    var target = _object_without_properties_loose(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _object_without_properties_loose(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState } from "react";
import { SOverlaySpinner } from "../SSpinner";
export const BaseButtonStyles = `
  flex
  flex-row
  gap-2
  justify-center
  items-center
  transition
  transition-all
  ease-in-out
  duration-100
  font-medium
  hover:opacity-90
  active:scale-90
  transform-gpu
`;
const VariantStyles = {
    primary: "bg-[rgba(116,255,241,0.1)] text-brand-primary",
    secondary: "bg-[rgba(163,163,163,0.1)] text-white",
    juicy: "bg-accent-light text-grayscale-darkest",
    tertiary: "cursor-pointer !px-3 !font-normal h-[32px] transition hover:!text-white/50 bg-grayscale-dark border border-white/10 !rounded-md flex flex-row items-center justify-between !text-[13px]"
};
const SizeStyles = {
    sm: "rounded-lg py-1 px-2 min-h-[26px] text-[11px]",
    md: "rounded-xl py-2 px-4 text-sm",
    lg: "rounded-lg py-1 px-2 text-base",
    xl: "rounded-lg py-1 px-2 text-lg"
};
const DisabledStyles = `
  pointer-events-none
  !opacity-50
`;
import { DynamicLink } from "../internal/DynamicLink";
import useHotKey, { formatKeyboardShortcut, useCommandKey } from "../hooks/useKeyboardShortcut";
import STooltip from "../Tooltip";
import useWindowSize from "../hooks/useWindowSize";
const ButtonLink = (_param)=>{
    var { href, linkProps, children } = _param, rest = _object_without_properties(_param, [
        "href",
        "linkProps",
        "children"
    ]);
    return /*#__PURE__*/ _jsx(DynamicLink, _object_spread_props(_object_spread({
        href: href
    }, linkProps, rest), {
        children: children
    }));
};
const compileButtonStyles = ({ className, variant = "primary", size = "md", isMinimal = false, disabled = false })=>{
    const variantStyles = VariantStyles[variant];
    const sizeStyles = SizeStyles[size];
    const minimalStyles = isMinimal ? "!bg-transparent" : "";
    const disabledStyles = disabled ? DisabledStyles : "";
    return `${BaseButtonStyles} ${variantStyles} ${sizeStyles} ${minimalStyles} ${disabledStyles} ${className} cursor-pointer`;
};
const SButton = (_param)=>{
    var { children, /**
   * Use `className` as a css override. Some class names may require the "!" tag
   * before the class name to fully override the base button styles or additional
   * styles that are added due to its variant and/or states.
   */ className, variant = "primary", hotkey: keyboardShortcut, tooltip, size = "md", isMinimal = false, disabled = false, type = "button", onClick, onCommandClick, isLoading: _isLoading, href, linkProps, target } = _param, props = _object_without_properties(_param, [
        "children",
        "className",
        "variant",
        "hotkey",
        "tooltip",
        "size",
        "isMinimal",
        "disabled",
        "type",
        "onClick",
        "onCommandClick",
        "isLoading",
        "href",
        "linkProps",
        "target"
    ]);
    const styles = compileButtonStyles({
        variant,
        size,
        isMinimal,
        disabled,
        className
    });
    const [animateButtonPress, setAnimateButtonPress] = useState(false);
    const [didReturnPromise, setDidReturnPromise] = useState(null);
    const isLoading = _isLoading || didReturnPromise;
    const { isCommandPressed } = useCommandKey();
    const screenSize = useWindowSize();
    const click = /*#__PURE__*/ function() {
        var _ref = _async_to_generator(function*(e) {
            const onClickUsing = isCommandPressed && onCommandClick !== undefined ? onCommandClick : onClick;
            if (onClickUsing) {
                // If we're currently awaiting a promise
                if (didReturnPromise) {
                    return;
                }
                if (e) {
                    e.preventDefault();
                }
                const onClickResult = onClickUsing(e);
                if (onClickResult instanceof Promise) {
                    if (e) e.preventDefault() // Prevent the default behavior of the button
                    ;
                    setDidReturnPromise(true);
                    yield onClickResult;
                    setDidReturnPromise(null);
                }
            }
        });
        return function click(e) {
            return _ref.apply(this, arguments);
        };
    }();
    useHotKey({
        hotkey: keyboardShortcut,
        action: /*#__PURE__*/ _async_to_generator(function*() {
            setAnimateButtonPress(true);
            yield click(undefined);
            setTimeout(()=>{
                setAnimateButtonPress(false);
            }, 100);
        })
    });
    if (typeof href !== "undefined") {
        return /*#__PURE__*/ _jsx(ButtonLink, {
            href: href,
            linkProps: linkProps,
            className: styles,
            onClick: onClick,
            target,
            children: children
        });
    }
    return /*#__PURE__*/ _jsx("button", _object_spread_props(_object_spread({
        type: type,
        onClick: click,
        className: styles
    }, props), {
        children: /*#__PURE__*/ _jsxs("div", {
            className: "relative",
            children: [
                /*#__PURE__*/ _jsx("div", {
                    style: {
                        opacity: isLoading ? 0 : 1,
                        transform: isLoading ? "scale(0)" : animateButtonPress ? "scale(0.9)" : "scale(1.0)",
                        transition: "transform 0.1s ease-in-out, opacity 0.1s ease-in-out"
                    },
                    className: " flex flex-row gap-2 justify-center items-center",
                    children: (keyboardShortcut || tooltip) && !(tooltip === null || tooltip === void 0 ? void 0 : tooltip.disabled) && ((screenSize === null || screenSize === void 0 ? void 0 : screenSize.width) || 100) > 768 ? /*#__PURE__*/ _jsx(STooltip, {
                        zIndex: 9999999,
                        delay: (tooltip === null || tooltip === void 0 ? void 0 : tooltip.delay) === undefined ? 500 : tooltip.delay,
                        placement: (tooltip === null || tooltip === void 0 ? void 0 : tooltip.placement) || "right",
                        content: /*#__PURE__*/ _jsx("div", {
                            className: "!max-w-[200px] inline",
                            children: /*#__PURE__*/ _jsxs("div", {
                                className: "-my-3 -mx-1 inline-flex items-center justify-center gap-3 ",
                                children: [
                                    (tooltip === null || tooltip === void 0 ? void 0 : tooltip.content) && // if tooltip is a string
                                    (typeof tooltip.content === "string" ? /*#__PURE__*/ _jsx("div", {
                                        className: "text-sm font-normal pl-1",
                                        children: tooltip.content
                                    }) : tooltip.content),
                                    keyboardShortcut && /*#__PURE__*/ _jsx("div", {
                                        className: "flex flex-row items-center justify-center gap-1",
                                        children: formatKeyboardShortcut(keyboardShortcut).map((key, index)=>/*#__PURE__*/ _jsx("div", {
                                                className: "text-white texm-xs bg-white/10 border border-white/10 font-medium tracking-wider rounded-md flex flex-row items-center justify-center w-[22px] h-[22px]",
                                                children: index === 0 ? /*#__PURE__*/ _jsx("span", {
                                                    children: key
                                                }) : /*#__PURE__*/ _jsx("span", {
                                                    className: "font-mono",
                                                    children: key
                                                })
                                            }, index))
                                    })
                                ]
                            })
                        }),
                        children: children
                    }) : children
                }),
                /*#__PURE__*/ _jsx("div", {
                    style: {
                        position: "absolute",
                        pointerEvents: "none",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        opacity: !isLoading ? 0 : 1,
                        transform: !isLoading ? "scale(0)" : "scale(1.0)",
                        transition: "transform 0.2s ease-in-out, opacity 0.2s ease-in-out"
                    },
                    children: /*#__PURE__*/ _jsx(SOverlaySpinner, {
                        overlayColor: "rgba(0,0,0,0)"
                    })
                })
            ]
        })
    }));
};
SButton.compileButtonStyles = compileButtonStyles;
export { SButton };
